<template>
  <div
    class="rounded-2xl p-6 flex flex-col justify-between min-h-[150px] transition-shadow duration-300"
    :class="dark ? 'bg-white/[0.07] border border-white/10 text-white' : 'bg-white border border-[#1c4848]/10 shadow-sm hover:shadow-md text-[#1c4848]'"
  >
    <div class="flex items-start justify-between gap-3">
      <p class="text-[15px] font-medium" :class="dark ? 'text-white/70' : 'text-[#1c4848]/60'">{{ label }}</p>
      <span
        v-if="badge"
        class="text-[13px] font-medium px-2.5 py-1 rounded-lg whitespace-nowrap"
        :class="badgeClass || (dark ? 'bg-white/10 text-white/80' : 'bg-[#1c4848]/5 text-[#1c4848]/60')"
      >
        {{ badge }}
      </span>
    </div>

    <div class="flex items-end justify-between gap-4 mt-4">
      <p class="text-[44px] leading-none font-semibold tracking-tight tabular-nums">{{ value }}</p>
      <svg v-if="spark.length > 1" :viewBox="`0 0 ${sw} ${sh}`" class="h-10 w-28 shrink-0" preserveAspectRatio="none">
        <defs>
          <linearGradient :id="gradId" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" :stop-color="sparkColor" stop-opacity="0.35" />
            <stop offset="1" :stop-color="sparkColor" stop-opacity="0" />
          </linearGradient>
        </defs>
        <path :d="areaPath" :fill="`url(#${gradId})`" />
        <path :d="linePath" fill="none" :stroke="sparkColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />
      </svg>
    </div>
    <p v-if="caption" class="text-[13px] mt-2" :class="dark ? 'text-white/50' : 'text-[#1c4848]/45'">{{ caption }}</p>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  value: { type: [String, Number], default: '' },
  label: { type: String, default: '' },
  badge: { type: String, default: '' },
  badgeClass: { type: String, default: '' },
  caption: { type: String, default: '' },
  /** Daily values drawn as a small line, for a sense of movement. */
  spark: { type: Array, default: () => [] },
  sparkColor: { type: String, default: '#ecd79f' },
  dark: { type: Boolean, default: false },
});

const sw = 112, sh = 40;
const gradId = `g${Math.random().toString(36).slice(2, 8)}`;

const points = computed(() => {
  const data = props.spark.map((v) => Number(v) || 0);
  const max = Math.max(...data, 1);
  const step = sw / Math.max(data.length - 1, 1);
  return data.map((v, i) => [i * step, sh - 3 - (v / max) * (sh - 6)]);
});
const linePath = computed(() => points.value.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' '));
const areaPath = computed(() => (points.value.length ? `${linePath.value} L${sw} ${sh} L0 ${sh} Z` : ''));
</script>
